import {
  BidderEvent,
  IdentityInfoEvent,
  InfoEvent,
  LabradorEvent,
  LoadTimeEvent,
  type LoadTimeTrackData,
  SizeInfoEvent,
  type TrackDataUnion,
  type TrackEvent,
  TrackingEvent,
} from "common-types";
import {
  isBidderEvent,
  isIdentityInfoEvent,
  isInfoEvent,
  isLabradorEvent,
  isLoadTimeEvent,
  isSizeInfoEvent,
  isTrackingEvent,
  waitForConsent,
  waitForFandomContextTracking,
} from "utils";
import { EligibleEventsTracker } from "../Tracker";
import { sendLoadTimes } from "./sender/loadTimesSender";
import { sendBidders } from "./sender/biddersSender";
import type { BeaconTracksMapping } from "./types";
import { SlotUid } from "./utils/slotUid";
import { sendAdSizeInfo } from "./sender/adSizeInfoSender";
import { sendAdInfo } from "./sender/infoSender";
import { sendLabradorKeyValues } from "./sender/labradorKeyValuesSender";
import { sendTrackingEvent } from "./sender/trackingEventSender";
import { sendIdentityInfo } from "./sender/identityInfoSender";

export class BeaconTracker extends EligibleEventsTracker {
  private readonly slotUid: SlotUid;
  private ready = false;
  private readonly readyPromise = Promise.all([
    waitForConsent(),
    waitForFandomContextTracking(),
  ]);

  protected readonly eligibleTrackEvents: TrackEvent[] = [
    LoadTimeEvent.AE_INIT,
    LoadTimeEvent.SLOT_REQUESTED,
    LoadTimeEvent.SLOT_RENDERED,
    LoadTimeEvent.VENDOR_TIME_UPDATE,
    SizeInfoEvent.SLOT_SIZE_ESTABLISHED,
    BidderEvent.BIDDER_RESPONSE,
    InfoEvent.SLOT_REQUESTED_INFO,
    InfoEvent.SLOT_RENDERED_INFO,
    InfoEvent.SLOT_CLICKED,
    InfoEvent.SLOT_FORCE_CLOSE_INFO,
    InfoEvent.SLOT_CODE_SERVED_INFO,
    IdentityInfoEvent.IDENTITY_INFO,
    LabradorEvent.LABRADOR_KEYVALUES,
    TrackingEvent.TRACKING_EVENT,
  ];

  constructor() {
    super();
    this.slotUid = new SlotUid();
  }

  trackEvent({ event, payload }: TrackDataUnion): void {
    if (!this.isEligible(event)) {
      return;
    }

    if (this.ready) {
      return this.sendEvent({ event, payload } as TrackDataUnion);
    }

    this.readyPromise.then(() => {
      this.ready = true;
      this.sendEvent({ event, payload } as TrackDataUnion);
    });
  }

  /**
   * Delegates the event to the appropriate sender handler
   */
  private sendEvent(fandomTrackData: TrackDataUnion): void {
    if (isInfoEvent(fandomTrackData)) {
      sendAdInfo(fandomTrackData, this.slotUid);
    } else if (isBidderEvent(fandomTrackData)) {
      sendBidders(fandomTrackData, this.slotUid);
    } else if (isLoadTimeEvent(fandomTrackData)) {
      this.sendLoadTimes(fandomTrackData);
    } else if (isSizeInfoEvent(fandomTrackData)) {
      sendAdSizeInfo(fandomTrackData, this.slotUid);
    } else if (isLabradorEvent(fandomTrackData)) {
      const { payload } = fandomTrackData;
      sendLabradorKeyValues({ payload });
    } else if (isIdentityInfoEvent(fandomTrackData)) {
      sendIdentityInfo(fandomTrackData);
    } else if (isTrackingEvent(fandomTrackData)) {
      sendTrackingEvent(fandomTrackData);
    }
    // fall through - this is an unsupported event.
    // This shouldn't be reachable if eligibleTrackEvents is accurate
    // TODO any sort of degradation alert here?
  }

  private sendLoadTimes(fandomTrackData: LoadTimeTrackData) {
    // NOTE: this config structure is being kept for now to
    // avoid refactoring the method signature of the sendLoadTimes
    // function itself, which currently expects a BeaconTracksMapping
    // object. A more consolidated implementation would be to remove
    // that parameter from sendLoadTimes and have the sendLoadTimes
    // function itself figure out the beaconEventName.
    const loadTimeConfig: BeaconTracksMapping = {
      [LoadTimeEvent.AE_INIT]: {
        type: "load_times",
        beaconEventName: "load_time_init",
      },
      [LoadTimeEvent.SLOT_REQUESTED]: {
        type: "load_times",
        beaconEventName: "requested",
      },
      [LoadTimeEvent.SLOT_RENDERED]: {
        type: "load_times",
        beaconEventName: "loaded",
      },
    };

    sendLoadTimes(fandomTrackData, loadTimeConfig[fandomTrackData.event]);
  }
}
